
import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
import { onBeforeRouteLeave, onBeforeRouteUpdate } from "vue-router";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import RecommendListTitle from "@/components/common/RecommendTitle.vue";
import BannerList from "@/components/home/BannerList.vue";
import BannerIndicator from "@/components/home/BannerIndicator.vue";
import ServiceTile from "@/components/home/ServiceTile.vue";
import RecipeTopList from "@/components/home/RecipeTopList.vue";
import RecommendFoodView from "@/components/home/RecommendFoodView.vue";
import RecommendFoodMenu from "@/components/home/RecommendFoodMenu.vue";
import store from "@/store";
import router from "@/router";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { PAGE_ID } from "@/const/log-const";
import {
  HOME_RECIPE_TYPE,
  HOME_BANNER_TYPE,
  BannerData,
  ServiceImageData,
  ServiceData,
  Banners,
  InformationServices,
  RequestFoodsRecommendationList,
  RequestOneMenuRecommendation,
  FoodRecommendationListData,
  OneMenuRecommendationData,
  FoodRecommendationList,
  FoodRecommendationReasonItem,
  FoodRecommendationListItem,
  RequestRecommendationRecipe,
  MenuDish
} from "@/types/home";
import NativeClient, { TitleInfo, BannerTapInfo } from "@/client/native-client";
import {
  createRecipeToCommonRecipesRecommend,
  createRecipeToCommonRecipe,
  getRecipeImageMain
} from "@/utils/search-util";
import {
  getFoodIds,
  getRecommendFoodList
} from "@/utils/home-util";
import CommonUtil from "@/utils/common-utils";
import { BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { CommonRecipe } from "@/types/commonRecipe";
import DateUtil from "@/utils/date-util";
import RecommendFoodNoticeToast from "@/components/home/RecommendFoodNoticeToast.vue"
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";

export default defineComponent({
  name: "HomePage",
  components: {
    RecommendListTitle,
    CommonRecipeList,
    BannerList,
    BannerIndicator,
    ServiceTile,
    RecipeTopList,
    RecommendFoodView,
    RecommendFoodMenu,
    RecommendFoodNoticeToast,
    CommonDesignDialog
  },
  setup() {
    const EMPTY_MESSAGE_1 = "食材の登録がありません";
    const EMPTY_MESSAGE_2 = "食材管理から食材を追加すると表示されます。";
    const ERROR_BUTTON_LABEL = "OK";
    const limitCount = 3;
    const bannerPosition = ref(0);
    const bannerTimerId = ref(-1);
    const bannerList = ref(Array<BannerData>());
    const serviceList = ref(Array<ServiceData>());
    const nativeToken = ref("");
    const visibled = ref(false);
    const topRecommendRecipe = ref(Array<CommonRecipe>());
    const recommendRecipes = ref(Array<CommonRecipe>());
    const topRankingRecipe = ref(Array<CommonRecipe>());
    const foodRecommendationData = ref(new FoodRecommendationListData(null));
    const oneMenuRecommendationData = ref(new OneMenuRecommendationData(null, null));
    const updateOneMenuKey = ref(0);
    const updateOneMenu = () => {
      if (updateOneMenuKey.value >= 100) {
        updateOneMenuKey.value = 0;
      } else {
        updateOneMenuKey.value++;
      }
    };
    const updateFoodKey = ref(0);
    const updateFood = () => {
      if (updateFoodKey.value >= 100) {
        updateFoodKey.value = 0;
      } else {
        updateFoodKey.value++;
      }
    };
    const refreshRecommendFoodList = ref(false);
    NativeClient.setTitle(new TitleInfo(true, "Live Pantry"));
    bannerPosition.value = store.getters["home/getBannerPosition"];

    // トースト関連
    const isShowToast = ref(false);
    const recommendFoodsUpdateMessage = "おすすめの内容を更新しています。";
    const closeToast = () => {
      NativeClient.sendDebugLog("HomePage closeToast receive enent");
      isShowToast.value = false;
    };
    const openToast = () => {
      NativeClient.sendDebugLog("HomePage openToast");
      isShowToast.value = true;
    };
    const checkUpdateRecommendFoods = (lastUpdateDate: string) => {
      NativeClient.sendDebugLog("HomePage checkUpdateRecommendFoods()");
      const recommendFoods: FoodRecommendationList = store.getters["home/getFoodRecommendationList"];
      if (recommendFoods != undefined && recommendFoods != null && recommendFoods.latestRecommended != undefined && recommendFoods.latestRecommended != null) {
        if (recommendFoods.latestRecommended.length > 0) {
          NativeClient.sendDebugLog("HomePage checkUpdateRecommendFoods() requestLoadUpdateDateRecommendFoods requestSaveUpdateDateRecommendFoods");
          NativeClient.requestSaveUpdateDateRecommendFoods(recommendFoods.latestRecommended);
        }
        if (recommendFoods.latestRecommended.length > 0 && lastUpdateDate.length > 0) {
          if (DateUtil.isPastRecommendFoodDateString(lastUpdateDate, recommendFoods.latestRecommended)) {
            // おすすめ食材が更新された
            NativeClient.sendDebugLog("HomePage checkUpdateRecommendFoods() update detect");
            openToast();
            return true;
          }
        }
      }
      NativeClient.sendDebugLog("HomePage checkUpdateRecommendFoods() no update");
      return false;
    }

    CommonUtil.scroll({ top: 0, left: 0 });

    const isLogin = () => {
      return nativeToken.value;
    };

    const setOneMenuRecommendation = () => {
      const result = store.getters["home/getOneMenuRecommendation"];
      NativeClient.sendDebugLog(
        "HomePage setOneMenuRecommendation result=" + JSON.stringify(result)
      );
      const dishs = Array<MenuDish>();
      if (result != null
          && result.recommends != null
          && result.recommends.length >= 1 ) {
        const recommend = result.recommends[0];
        if (recommend != null && recommend.recipes != null) {
          NativeClient.sendDebugLog(
            "HomePage setOneMenuRecommendation recommend.recipes=" + JSON.stringify(recommend.recipes)
          );
          for (const item of recommend.recipes) {
            const foodList = getFoodIds(item, foodRecommendationData.value);
            const dish = new MenuDish(item, foodList);
            dishs.push(dish);
          }
        }
      }
      NativeClient.sendDebugLog(
        "HomePage setOneMenuRecommendation commonRecipe=" + JSON.stringify(dishs)
      );
      oneMenuRecommendationData.value.dishs = dishs;
      oneMenuRecommendationData.value.response = result;
      updateOneMenu();
    };

    const setFoodRecommendation = () => {
      const result:FoodRecommendationList = store.getters["home/getFoodRecommendationList"];
      NativeClient.sendDebugLog(
        "HomePage setFoodRecommendation result=" + JSON.stringify(result)
      );
      foodRecommendationData.value.response = result;
      NativeClient.sendDebugLog(
        "HomePage setFoodRecommendation 2 result=" + JSON.stringify(result)
      );
    };

    // おすすめレシピリストを取得(TODO)
    const getRecommendRecipes = () => {
      const result = store.getters["home/getRecommendRecipes"];
      NativeClient.sendDebugLog(
        "HomePage getRecommendRecipes result=" + JSON.stringify(result)
      );
      topRecommendRecipe.value.length = 0;
      recommendRecipes.value.length = 0;
      const commonRecipe = Array<CommonRecipe>();
      if (result != null && result.recipes != null) {
        const size = result.recipes.length >= 4 ? 4 : result.recipes.length;
        for (let i = 1; i < size; i++) {
          const foodList = getFoodIds(result.recipes[i], foodRecommendationData.value);
          const recipe = createRecipeToCommonRecipe(result.recipes[i], foodList);
          commonRecipe.push(recipe);
        }
        NativeClient.sendDebugLog(
          "HomePage getRecommendRecipes commonRecipe=" + JSON.stringify(commonRecipe)
        );
        if (result.recipes.length >= 1) {
          const foodList = getFoodIds(result.recipes[0], foodRecommendationData.value);
          const recipe = createRecipeToCommonRecipe(result.recipes[0], foodList);
          recipe.imageUrl = getRecipeImageMain(result.recipes[0].images);
          topRecommendRecipe.value.push(recipe);
        }
        recommendRecipes.value = commonRecipe;
      }
    };

    /**
     * おすすめ食材を元にレシピリストを取得する
     */
    const getRecommendFoodRecipes = async (isRefresh: boolean, isUpdateRecommendFoods: boolean) => {
      NativeClient.sendDebugLog("HomePage getRecommendFoodRecipes isRefresh = " + isRefresh + ", isUpdateRecommendFoods = " + isUpdateRecommendFoods);
      setFoodRecommendation();
      const foodList = getRecommendFoodList(foodRecommendationData.value);
      const nowDate = DateUtil.getToday();
      const payload = new RequestOneMenuRecommendation(nowDate, (isUpdateRecommendFoods), foodList);
      const recipePayload = new RequestRecommendationRecipe(foodList, isUpdateRecommendFoods);
      NativeClient.sendDebugLog("HomePage getRecommendFoodRecipes recipePayload =" + JSON.stringify(recipePayload));
      NativeClient.sendDebugLog("HomePage getRecommendFoodRecipes payload = " + JSON.stringify(payload));
      await Promise.all([
        store.dispatch("home/getRecommendRecipes", recipePayload),
        store.dispatch("home/getOneMenuRecommendation", payload)
      ]).then(result => {
        setOneMenuRecommendation();
        getRecommendRecipes();
        updateFood();
        store.dispatch("common/stopLoading");
      });
    };

    /**
     * レシピリストを取得する
     */
    const getRecipes = async (isRefresh: boolean) => {
      NativeClient.sendDebugLog("HomePage getRecipes() isRefresh=" + isRefresh);
      refreshRecommendFoodList.value = isRefresh;
      store.dispatch("common/startLoading");
      await Promise.all([
        NativeClient.getApiParams(),
        NativeClient.getUpdateDateRecommendFoods()
      ]).then(result => {
        const apiParams = store.getters["common/getApiBaseParams"];
        NativeClient.sendDebugLog(
          "HomePage getRecipes apiParams=" + JSON.stringify(apiParams)
        );
        const lastUpdateDateRecommendFoods = `${result[1]}`;
        NativeClient.sendDebugLog(
          "HomePage getRecipes lastUpdateDateRecommendFoods = " + lastUpdateDateRecommendFoods
        );
        nativeToken.value = apiParams.token;
        const foodRecomendParameter = new RequestFoodsRecommendationList();
        foodRecomendParameter.isRefresh = isRefresh;
        if (lastUpdateDateRecommendFoods != undefined && lastUpdateDateRecommendFoods != null && lastUpdateDateRecommendFoods.length > 0) {
          foodRecomendParameter.latestRecommended = lastUpdateDateRecommendFoods
        }
        if (isLogin()) {
          Promise.all([
            store.dispatch("home/getFoodRecommendationList", foodRecomendParameter),
            // store.dispatch("home/getBanners"),
            // store.dispatch("home/getInformationServices"),
          ]).then(result => {
            const isUpdateRecomendFoods = checkUpdateRecommendFoods(lastUpdateDateRecommendFoods);
            getRecommendFoodRecipes(isRefresh, isUpdateRecomendFoods);
          });
        } else {
          NativeClient.sendDebugLog("HomePage getRecipes() not login isRefresh = " + isRefresh);
          Promise.all([
            store.dispatch("home/getRecommendRecipes", new RequestRecommendationRecipe(null, isRefresh)),
            // store.dispatch("home/getBanners"),
            // store.dispatch("home/getInformationServices")
          ]).then(result => {
            getRecommendRecipes();
            store.dispatch("common/stopLoading");
          });
        }
      });
    };

    const getMenu = async () => {
      NativeClient.sendDebugLog("HomePage getMenu");
      store.dispatch("common/startLoading");
      await Promise.all([NativeClient.getApiParams()]).then(result => {
        const apiParams = store.getters["common/getApiBaseParams"];
        NativeClient.sendDebugLog(
          "HomePage getMenu apiParams=" + JSON.stringify(apiParams)
        );
        nativeToken.value = apiParams.token;
        const foodList = getRecommendFoodList(foodRecommendationData.value);
        const nowDate = DateUtil.getToday();
        // ここは、「おすすめ食材を使った献立」で「更新」ボタンがタップされた場合の処理なので、refreshはtrue
        const payload = new RequestOneMenuRecommendation(nowDate, true, foodList);
        NativeClient.sendDebugLog("HomePage getRecipes nowDate=" + nowDate);
        Promise.all([
          store.dispatch("home/getOneMenuRecommendation", payload)
        ]).then(result => {
          setOneMenuRecommendation();
          store.dispatch("common/stopLoading");
        });
      });
    };

    /**
     * レシピリストの取得を実行する
     */
    const execute = async () => {
      getRecipes(false);
    };

    const setScrollTimer = () => {
      if (bannerTimerId.value == -1) {
        bannerTimerId.value = window.setInterval(() => {
          let position = bannerPosition.value;
          position++;
          if (position >= bannerList.value.length) {
            position = 0;
          }
          bannerPosition.value = position;
        }, 5000);
        console.trace();
        NativeClient.sendDebugLog(
          "HomePage setScrollTimer bannerTimerId.value=" + bannerTimerId.value
        );
      }
    };

    const clearScrollTimer = () => {
      NativeClient.sendDebugLog(
        "HomePage clearScrollTimer bannerTimerId.value=" + bannerTimerId.value
      );
      if (bannerTimerId.value != -1) {
        clearInterval(bannerTimerId.value);
      }
      bannerTimerId.value = -1;
    };

    const swipeRight = () => {
      let position = bannerPosition.value;
      position--;
      if (position <= -1) {
        position = bannerList.value.length - 1;
      }
      NativeClient.sendDebugLog(
        "HomePage swipeRight old=" + position + " new=" + bannerPosition.value
      );
      bannerPosition.value = position;
      clearScrollTimer();
      setScrollTimer();
    };

    const swipeLeft = () => {
      let position = bannerPosition.value;
      position++;
      if (position >= bannerList.value.length) {
        position = 0;
      }
      NativeClient.sendDebugLog(
        "HomePage swipeLeft new=" + position + " old=" + bannerPosition.value
      );
      bannerPosition.value = position;
      NativeClient.sendDebugLog(
        "HomePage swipeLeft old=" + position + " new=" + bannerPosition.value
      );
      clearScrollTimer();
      setScrollTimer();
    };

    const isShowBanner = () => {
      // バナー非表示
      // return bannerList.value.length >= 1;
      return false;
    };

    const isShowBannerIndicator = () => {
      // バナー非表示
      // return bannerList.value.length >= 2;
      return false;
    };

    const isShowService = () => {
      // サービス非表示
      // return serviceList.value.length >= 1;
      return false;
    };

    const setGone = () => {
      NativeClient.sendDebugLog("HomePage setGone");
      clearScrollTimer();
      visibled.value = false;
    };

    const setVisible = () => {
      NativeClient.sendDebugLog("HomePage setVisible");
      if (isShowBannerIndicator()) {
        setScrollTimer();
      }
      visibled.value = true;
    };

    const updateHomeState = () => {
      NativeClient.sendDebugLog("HomePage updateHomeState");
      // 画面切り替え通知
      setVisible();
    };

    // ホーム状態切り替えを登録
    NativeClient.setHomeState(updateHomeState);

    // 食材管理（パントリー）で変更があった場合の処理
    const updatePantryListAction = () => {
      NativeClient.sendDebugLog("HomePage updatePantryListAction");
      // refreshをtrueに設定
      getRecipes(true);
    };

    /**
     * コンポーネントがマウントされたときに通知される
     */
    onMounted(() => {
      const beaconLogInfo = new BeaconLogInfo(PAGE_ID.HOME_TOP, null, new Array<string>());
      NativeClient.sendViewedLog(beaconLogInfo);
      setVisible();
      execute();

      // 食材管理（パントリー）で変更があった場合の処理登録
      NativeClient.updatePantryList(updatePantryListAction);
    });

    const saveBanner = () => {
      store.dispatch("home/updateBannerPosition", bannerPosition.value);
    };

    onUnmounted(() => {
      setGone();
      if (isShowBannerIndicator()) {
        saveBanner();
      }
    });

    onBeforeRouteLeave((to, from) => {
      NativeClient.sendDebugLog("HomePage onBeforeRouteLeave");
      setGone();
      if (isShowBannerIndicator()) {
        saveBanner();
      }
    });

    onBeforeRouteUpdate((to, from) => {
      NativeClient.sendDebugLog("HomePage onBeforeRouteUpdate");
    });

    /** APIリクエスト時のエラーハンドル */
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();

    /** ダイアログの制御 */
    const { closeDialog } = useApiErrorDialog();

    // 人気レシピリストを取得(TODO)
    const rankingRecipes = computed(() => {
      const result = store.getters["home/getRankingRecipes"];
      topRankingRecipe.value.length = 0;
      if (result != null && result.recipes != null) {
        const commonRecipe = createRecipeToCommonRecipesRecommend(result.recipes);
        NativeClient.sendDebugLog(
          "HomePage computed rankingRecipes " + JSON.stringify(commonRecipe)
        );
        if (result.recipes.length >= 1) {
          const recipe = createRecipeToCommonRecipe(result.recipes[0], []);
          recipe.imageUrl = getRecipeImageMain(result.recipes[0].images);
          topRankingRecipe.value.push(recipe);
        }
        return commonRecipe;
      }
      NativeClient.sendDebugLog("HomePage computed rankingRecipes empty");
      return null;
    });

    const banners = computed(() => {
      const result: Banners = store.getters["home/getBanners"];
      bannerList.value.length = 0;
      if (result != null && result.banners != null) {
        if (result.banners.length >= 1) {
          let i = 0;
          result.banners.forEach(e => {
            const bannerData = new BannerData(
              i,
              e.imageUrl,
              e.contentsUrl,
              e.externalContentsFlg
            );
            bannerList.value.push(bannerData);
            i++;
          });
        }
      }
      if (isShowBannerIndicator()) {
        if (visibled.value && bannerTimerId.value == -1) {
          setScrollTimer();
        }
      }
      return null;
    });

    const services = computed(() => {
      const result: InformationServices =
        store.getters["home/getInformationServices"];
      serviceList.value.length = 0;
      if (result != null && result.services != null) {
        if (result.services.length >= 1) {
          let i = 0;
          result.services.forEach(e => {
            const serviceImageData = new ServiceImageData(null, null);
            if (e.image != null) {
              serviceImageData.top = e.image.top;
              serviceImageData.thumb = e.image.thumb;
            }
            const serviceData = new ServiceData(
              i,
              e.title,
              e.description,
              serviceImageData,
              e.contentsUrl,
              e.externalContentsFlg
            );
            serviceList.value.push(serviceData);
            i++;
          });
        }
      }
      return null;
    });

    /**
     * 選択されたレシピをNativeに通知する
     */
    const selectRecipe = (id: number, type: string, objectId: string) => {
      // Native側にレシピ詳細画面に遷移指示を出す
      NativeClient.sendDebugLog(
        "HomePage selectRecipe id=" + id + " type=" + type
      );
      const viewedItem = Array<string>();
      viewedItem.push(String(id));
      const beaconLogInfo = new BeaconLogInfo(PAGE_ID.HOME_TOP, objectId, viewedItem);
      NativeClient.sendClickedActionLog(beaconLogInfo);
      NativeClient.transitionReceipDetail(id, type);
      setGone();
    };

    const selectRecipeRecommend = (id: number, type: string) => {
      selectRecipe(id, type, "btn_2_B-21_RecommendRecipe");
    };

    const selectRecipeOneMenu = (id: number, type: string) => {
      NativeClient.sendDebugLog(
        "HomePage selectRecipeOneMenu id=" + id + " type=" + type
      ); 
      NativeClient.transitionReceipDetail(id, type);
      setGone();
    };

    /**
     * 選択されたレシピ種別からレシピ一覧画面を表示(TODO)
     */
    const clickSeeMore = (type: string) => {
      NativeClient.sendDebugLog("HomePage clickSeeMore type=" + type);
      switch (type) {
        case HOME_RECIPE_TYPE.RECOMMEND:
          router.push(`/home-recommend?type=` + type);
          break;
        case HOME_RECIPE_TYPE.SERVICE:
          router.push(`/home-service`);
          break;
      }
    };

    const clickStock = () => {
      const bannerTapInfo = new BannerTapInfo(HOME_BANNER_TYPE.PANTRY, null);
      NativeClient.jumpBannerTapEvent(bannerTapInfo);
    };

    /**
     * 選択されたレシピのタグでレシピ検索を実行する
     *
     * @param {string} tag - タグ名
     */
    const selectBanner = (id: number) => {
      let url = null;
      let extenalUrl = null;
      bannerList.value.forEach(e => {
        if (e.id == id) {
          url = e.contentsUrl;
          if (url != null) {
            // 外部ブラウザ起動
            if (e.externalContentsFlg) {
              extenalUrl = url;
              url += "?window=browser";
            } else {
              url += "?window=webview";
            }
          }
        }
      });
      if (url == null) {
        NativeClient.sendDebugLog(
          "HomePage selectBanner id=" + id + " url empty"
        );
        return;
      }
      NativeClient.sendDebugLog(
        "HomePage selectBanner id=" + id + " url=" + url
      );
      const bannerId = id + 1;
      const objectId = "btn_1_B-21_Banner_" + bannerId;
      const viewedItem = Array<string>();
      if (extenalUrl != null) {
        viewedItem.push(extenalUrl);
      }
      const beaconLogInfo = new BeaconLogInfo(PAGE_ID.HOME_TOP, objectId, viewedItem);
      NativeClient.sendClickedActionLog(beaconLogInfo);
      location.href = url;
      setGone();
    };

    /**
     * 選択されたurlをNativeに通知する
     */
    const selectService = (id: number) => {
      let url = null;
      let extenalUrl = null;
      let title = null;
      serviceList.value.forEach(e => {
        if (e.id == id) {
          url = e.contentsUrl;
          title = e.title;
          if (url != null) {
            extenalUrl = url;
            // 外部ブラウザ起動
            if (e.externalContentsFlg) {
              url += "?window=browser";
            } else {
              url += "?window=webview";
            }
          }
        }
      });
      if (url == null) {
        NativeClient.sendDebugLog(
          "HomePage selectService id=" + id + " url empty"
        );
        return;
      }
      NativeClient.sendDebugLog(
        "HomePage selectService id=" + id + " url=" + url
      );
      const serviceId = id + 1;
      const objectId = "btn_1_B-21_ServiceIcon_" + serviceId;
      const viewedItem = Array<string>();
      if (extenalUrl != null) {
        viewedItem.push(extenalUrl);
      }
      const beaconLogInfo = new BeaconLogInfo(PAGE_ID.HOME_TOP, objectId, viewedItem);
      NativeClient.sendClickedActionLog(beaconLogInfo);
      location.href = url;
      setGone();
    };

    const isShowTopRecommendRecipe = () => {
      return topRecommendRecipe.value.length != 0;
    };

    const isShowTopRankingRecipe = () => {
      return topRankingRecipe.value.length != 0;
    };

    const isShowRecommendFood = computed(() => {
      return isLogin() 
        && foodRecommendationData.value.response
        && foodRecommendationData.value.response.foods
        && foodRecommendationData.value.response.foods.length != 0;
    });

    const isShowRecommendFoodEmpty = computed(() => {
      return !isLogin() 
        || foodRecommendationData.value.response
        && foodRecommendationData.value.response.foods
        && foodRecommendationData.value.response.foods.length == 0;
    });

    const isShowRecommendFoodMenu = computed(() => {
      return isLogin() 
        && oneMenuRecommendationData.value.response
        && oneMenuRecommendationData.value.response.recommends
        && oneMenuRecommendationData.value.response.recommends.length != 0;
    });

    const isShowRecommendFoodMenuEmpty = computed(() => {
      return !isLogin() 
        || oneMenuRecommendationData.value.response
        && oneMenuRecommendationData.value.response.recommends
        && oneMenuRecommendationData.value.response.recommends.length == 0;
    });

    const clickUpdateMenu = () => {
      getMenu();
    };

    const selectFood = (id: string, type: string, foodId: string) => {
      NativeClient.sendDebugLog(
        "HomePage selectFood id=" + id + " type=" + type + " foodId=" + foodId
      );
      if (id != "") {
        NativeClient.transitionPantryDetail(id, type, "");
      } else {
        NativeClient.transitionPantryPRFood(foodId);
      }
    };

    const closeError = () => {
      closeDialog();
    };

    return {
      isShowToast,
      recommendFoodsUpdateMessage,
      closeToast,
      recommendRecipes,
      rankingRecipes,
      limitCount,
      clickSeeMore,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      closeDialog,
      getRecipes,
      HOME_RECIPE_TYPE,
      banners,
      bannerList,
      bannerPosition,
      selectBanner,
      isShowBanner,
      isShowBannerIndicator,
      services,
      serviceList,
      selectService,
      isShowService,
      isLogin,
      swipeRight,
      swipeLeft,
      topRecommendRecipe,
      isShowTopRecommendRecipe,
      topRankingRecipe,
      isShowTopRankingRecipe,
      selectRecipeRecommend,
      isShowRecommendFood,
      isShowRecommendFoodEmpty,
      isShowRecommendFoodMenu,
      isShowRecommendFoodMenuEmpty,
      foodRecommendationData,
      oneMenuRecommendationData,
      updateOneMenuKey,
      selectRecipeOneMenu,
      clickUpdateMenu,
      updateFoodKey,
      selectFood,
      clickStock,
      EMPTY_MESSAGE_1,
      EMPTY_MESSAGE_2,
      closeError,
      ERROR_BUTTON_LABEL
    };
  }
});
